import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Breadcrumb, type BreadcrumbProps } from '@naf-simba/sections-breadcrumbs';
import useBreadcrumbs from 'use-react-router-breadcrumbs';
import { isNotTransientProp } from '../../lib/shouldForwardProp';
import routeConfig from '../../config/routeConfig';
import useSelector from '../../redux/typedHooks';
import { BreadcrumbLoader } from '../skeleton-loaders/BreadcrumbLoader';

interface BreadCrumpProps {
  paddingTop?: boolean;
  className?: string;
}

export const BreadCrumb = ({ paddingTop = true, className }: BreadCrumpProps) => {
  const generatedCrumbs = useBreadcrumbs(routeConfig);
  const categoriesState = useSelector((state) => state.sitestructure?.mappedCategories);
  const documentState = useSelector((state) => state.documents?.mappedData);

  // Do not simplify this - it caused the webapp to break in production
  const [shouldShowBreadcrumbs, setShouldShowBreadcrumbs] = useState(false);
  useEffect(() => {
    setShouldShowBreadcrumbs(generatedCrumbs?.length > 1);
  }, [generatedCrumbs]);
  // Do not simplify this - end

  // Simba renders the last entry as plain text rather than a link, which is what the legacy
  // component did for the crumb matching the current location.
  const items = generatedCrumbs.reduce<BreadcrumbProps['items']>((acc, { match, breadcrumb }) => {
    if (breadcrumb && React.isValidElement<{ children?: string }>(breadcrumb)) {
      // Do some lookups to get prettier names, fallback to slug/url
      const bcSlug = breadcrumb.props.children && breadcrumb.props.children.toLowerCase();
      const catName = bcSlug && categoriesState && categoriesState[bcSlug];
      const documentName = bcSlug && documentState && documentState[bcSlug];
      const slugName = breadcrumb.props.children
        ? breadcrumb.props.children
            .replace(/-/g, ' ')
            .replace(/(?<![A-Za-zÆØÅæøå])[nN][aA][fF](?![A-Za-zÆØÅæøå])/g, 'NAF') // makes all variants of lowercase naf into NAF
        : '';
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const bcName = catName?.data?.name || documentName?.data?.name || documentName?.data?.title || slugName;

      acc.push({ text: bcName, to: match.pathname });
    }
    return acc;
  }, []);

  return shouldShowBreadcrumbs ? (
    <StyledBreadcrumbs $paddingTop={paddingTop} className={className} items={items} />
  ) : (
    <StyledSkeleton $paddingTop={paddingTop}>{!generatedCrumbs && <BreadcrumbLoader />}</StyledSkeleton>
  );
};

const StyledSkeleton = styled.div<{ $paddingTop?: boolean }>`
  padding-top: ${(props) => (props.$paddingTop ? '24px' : '0')};
`;

const StyledBreadcrumbs = styled(Breadcrumb, { shouldForwardProp: isNotTransientProp })<{ $paddingTop?: boolean }>`
  padding-top: ${(props) => (props.$paddingTop ? '24px' : '0')};
`;

export default BreadCrumb;
